
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

export function SortableRow(props) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: props.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 999 : 'auto',
    };

    return (
        <tr ref={setNodeRef} style={style} className={`${props.className} ${isDragging ? 'bg-blue-50 shadow ring-1 ring-blue-200' : ''}`}>
            {/* Drag Handle Cell replaced the "No." cell usage in the parent, but here we just render the handle in the first cell passed or as a dedicated handle?
                Actually, the plan is to REPLACE the first column or ADD a handle.
                Let's stick to the plan: "No." column is fixed 1,2,3.
                We need a dedicated handle column OR use the "No." column as handle.
                Let's use a specific handle column or make the whole row draggable?
                Better to have a specific handle for accessibility and avoiding conflict with inputs.
                I will render the handle as a <td> if I put it here?
                No, this component wraps <tr>. The children are <td>s.
                So I should probably inject the handle <td> as the first child, or let the parent render it.
                
                Let's let the parent render the content, but we need to expose the drag handle listeners.
                
                Option A: Pass a `Handle` component to the children.
                Option B: `SortableRow` renders the `tr` and passes `attributes` and `listeners` to a specific child if we want a handle, 
                          OR we apply listeners to the whole row (might conflict with inputs).
                
                Best approach: Pass `dragHandleProps` to the children, or have a specific slot.
                
                Actually, simpler: `SortableRow` wraps the `tr`. It provides a render prop or just accepts children.
                The handle needs `attributes` and `listeners`.
                
                Let's rewrite `SortableRow` to export a `DragHandle` component or pass these down.
                
                Actually, commonly we just expose the hooks. But since I am making a helper component:
            */}
            {/* We will insert the handle as the first TD automatically? No, that messes up the table structure if headers rely on it.
                Let's make SortableRow just provide the ref and style to the TR.
                And validly allow a "dragHandle" prop or let the user choose.
            */}
            {React.Children.map(props.children, child => {
                if (React.isValidElement(child) && child.props['data-drag-handle']) {
                    return React.cloneElement(child, { ...attributes, ...listeners });
                }
                return child;
            })}
        </tr>
    );
}

// Simpler version: The handle is a separate small component or we just export the hook.
// But to keep it effectively simple for `TemplateManagement.jsx` refactor:

export const SortableRowContext = React.createContext({ attributes: {}, listeners: {} });

export function SortableRowWrapper({ id, children, className, style: propStyle }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 999 : 'auto',
        position: 'relative',
        ...propStyle
    };

    return (
        <SortableRowContext.Provider value={{ attributes, listeners, isDragging }}>
            <tr ref={setNodeRef} style={style} className={className}>
                {children}
            </tr>
        </SortableRowContext.Provider>
    );
}

export function DragHandle({ className, children }) {
    const { attributes, listeners } = React.useContext(SortableRowContext);
    return (
        <div {...attributes} {...listeners} className={`cursor-grab touch-none ${className}`}>
            {children || <GripVertical className="w-4 h-4 text-gray-400 hover:text-blue-600" />}
        </div>
    );
}
